// ABOUTME: Persistent v28 header -- waʻa logo and title, centered "CROSSING #n · CHILD" line, and the right cluster
// ABOUTME: (Steersman toggle slot + Save & Exit, which leaves via route navigation) (Issues #1940, #2175).

import type { ReactNode } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  CROSSING_BORDER_HEADER,
  CROSSING_BORDER_PILL,
  CROSSING_FOCUS_RING,
  CROSSING_HEADER_EYEBROW_INK,
  CROSSING_HEADLINE_INK,
  CROSSING_PAPER,
} from '../../../theme/crossingTokens';
import { usePhoneLayout } from '../runner/usePhoneLayout';

interface CrossingHeaderProps {
  // The child's crossing count from the server (their 1-based position among all of that child's crossings), never the
  // database id. v28 wins over the 2026-09-22 feedback that dropped the number (SPEC section 0); bryan decided on
  // 2026-10-30 that the header shows "CROSSING #{n} · CHILD".
  crossingNumber: number;
  // Upper-cased here; the caller supplies the name or its "Child {id}" fallback.
  childName: string;
  returnHref: string;
  // Passed through to navigate()'s state -- e.g. { activeChildId } so the parent dashboard can
  // restore the tab the parent was on before opening Crossing (tabs are component state, not URL).
  returnState?: Record<string, unknown>;
  rightSlot?: ReactNode;
  // The runner frame owns its exit: it supplies its own pill label and takes over the click (to confirm
  // unsent picks before leaving). Without them the pill reads "Save & Exit" and navigates.
  exitLabel?: string;
  onExit?: () => void;
}

export default function CrossingHeader({ crossingNumber, childName, returnHref, returnState, rightSlot, exitLabel, onExit }: CrossingHeaderProps) {
  const navigate = useNavigate();
  const phone = usePhoneLayout();

  return (
    <Box
      data-testid="crossing-header"
      sx={{
        position: 'relative',
        zIndex: 30,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
        height: 46,
        backgroundColor: CROSSING_PAPER,
        borderBottom: `1px solid ${CROSSING_BORDER_HEADER}`,
        px: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '1 1 0', minWidth: 0 }}>
        <Box component="img" data-testid="crossing-logo" src="/nalu/logo-waa-256.png" alt="" sx={{ width: 22, height: 22 }} />
        {!phone && (
          <Typography sx={{ fontFamily: 'Newsreader, serif', fontSize: '17px', fontWeight: 600, color: CROSSING_HEADLINE_INK }}>
            {"Let's Reset"}
          </Typography>
        )}
      </Box>

      <Typography
        data-testid="crossing-eyebrow"
        sx={{ fontSize: '11px', fontWeight: 800, letterSpacing: '2px', textAlign: 'center', color: CROSSING_HEADER_EYEBROW_INK, whiteSpace: 'nowrap' }}
      >
        {`CROSSING #${crossingNumber} · ${childName.toUpperCase()}`}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flex: '1 1 0', minWidth: 0 }}>
        {rightSlot}
        <Chip
          label={exitLabel ?? 'Save & Exit'}
          variant="outlined"
          onClick={onExit ?? (() => navigate(returnHref, { replace: true, state: returnState }))}
          role="button"
          sx={{
            backgroundColor: CROSSING_PAPER,
            border: `1px solid ${CROSSING_BORDER_PILL}`,
            '&.Mui-focusVisible, &:focus-visible': { outline: `2px solid ${CROSSING_FOCUS_RING}`, outlineOffset: 2 },
          }}
        />
      </Box>
    </Box>
  );
}
